import React, { useEffect, useState } from 'react';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import ZoomImage from './feed/ZoomImage';

/**
 * The full receipt behind a Purchase History row (Brad, 2026-09-23: "link the receipt
 * here to open and see full details of what they purchased").
 *
 * Two kinds, read from where the record already lives - no copy:
 *   pos       our own register sale, straight from the POS ledger, the same payload the
 *             printer used (lines, tax, discounts, payments, cashier, time);
 *   purchase  a receipt scan (with its image) or a home delivery, with its lines.
 */
export interface ReceiptRef { kind: 'pos' | 'purchase'; id: number }

interface PosLine { name: string; quantity: number; unit: string; unit_price: number; total: number; discount_amount?: number; bag_credit_label?: string }
interface PosReceipt {
  store_name: string; store_address: string; transaction_number: string; date: string; time: string; cashier: string;
  items: PosLine[]; subtotal: number; tax: number; discount: number; surcharge: number; total: number;
  payments: { method: string; amount: number; card_type?: string | null; card_last_four?: string | null; change?: number | null }[];
}
interface PurchaseLine { product_name: string; quantity: number; unit_price: string | number; total_price: string | number; is_red_bridge: boolean; weight_lbs?: string | number | null }
interface PurchaseReceipt {
  id: number; purchase_date: string; receipt_image?: string | null; receipt_number?: string | null; notes?: string;
  submission_type: string; subtotal: string | number; tax: string | number; total: string | number;
  red_bridge_bags: number; items: PurchaseLine[]; store?: { name?: string; public_store_name?: string; city?: string } | null;
}

const RED = '#B91C1C';
const money = (v: string | number | null | undefined) => `$${Number(v || 0).toFixed(2)}`;
const muted: React.CSSProperties = { color: '#6b7280', fontSize: 13 };
const row: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 0', borderTop: '1px solid #f3f4f6', fontSize: 15 };

const ReceiptDialog: React.FC<{ receipt: ReceiptRef; onClose: () => void }> = ({ receipt, onClose }) => {
  const [pos, setPos] = useState<PosReceipt | null>(null);
  const [purchase, setPurchase] = useState<PurchaseReceipt | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    const load = receipt.kind === 'pos'
      ? bridgeClubAPI.getPosReceipt(receipt.id).then((r: PosReceipt) => { if (alive) setPos(r); })
      : bridgeClubAPI.getPurchase(receipt.id).then((r: PurchaseReceipt) => { if (alive) setPurchase(r); });
    load.catch(() => { if (alive) setError("We couldn't open that receipt. Please try again."); });
    return () => { alive = false; };
  }, [receipt]);

  const loaded = pos || purchase;
  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 16px 100px' }}
    >
      <div
        role="dialog"
        aria-label="Receipt"
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid #eee', flex: '0 0 auto' }}>
          <h3 style={{ margin: 0, fontSize: 18, color: RED }}>
            {pos ? 'Red Bridge Farm store' : purchase?.notes?.startsWith('Home delivery') ? 'Home delivery'
              : purchase?.store ? (purchase.store.public_store_name || purchase.store.name) : 'Receipt'}
          </h3>
          {pos && <div style={muted}>{pos.date} at {pos.time} · Receipt #{pos.transaction_number} · {pos.cashier}</div>}
          {purchase && <div style={muted}>{purchase.purchase_date}{purchase.receipt_number ? ` · Receipt #${purchase.receipt_number}` : ''}</div>}
        </div>

        <div style={{ padding: '10px 16px', overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
          {error && <p style={{ color: '#b91c1c', fontWeight: 600 }}>{error}</p>}
          {!loaded && !error && <p style={muted}>Opening the receipt…</p>}

          {pos && (
            <>
              {pos.items.map((l, i) => (
                <div key={i} style={row}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{l.name}</div>
                    <div style={muted}>
                      {l.quantity} {l.unit} @ {money(l.unit_price)}{l.unit === 'lb' ? '/lb' : ''}
                      {l.discount_amount ? ` · ${money(l.discount_amount)} off` : ''}
                      {l.bag_credit_label ? ` · ${l.bag_credit_label}` : ''}
                    </div>
                  </div>
                  <div style={{ fontVariantNumeric: 'tabular-nums' }}>{money(l.total)}</div>
                </div>
              ))}
              <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 6, paddingTop: 8, fontSize: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><span>{money(pos.subtotal)}</span></div>
                {pos.discount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Discount</span><span>-{money(pos.discount)}</span></div>}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Tax</span><span>{money(pos.tax)}</span></div>
                {pos.surcharge > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Card surcharge</span><span>{money(pos.surcharge)}</span></div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: 4, fontSize: 16 }}><span>Total</span><span>{money(pos.total)}</span></div>
              </div>
              {pos.payments.length > 0 && (
                <div style={{ ...muted, marginTop: 8 }}>
                  Paid by {pos.payments.map((p) => `${p.method}${p.card_type ? ` ${p.card_type}` : ''}${p.card_last_four ? ` ····${p.card_last_four}` : ''} ${money(p.amount)}`).join(', ')}
                </div>
              )}
              <div style={{ ...muted, marginTop: 8 }}>{pos.store_name} · {pos.store_address}</div>
            </>
          )}

          {purchase && (
            <>
              {purchase.receipt_image && (
                <div style={{ marginBottom: 10 }}>
                  <ZoomImage src={purchase.receipt_image} alt="Receipt" size={96} fit="cover" />
                  <div style={muted}>Tap the receipt to read it.</div>
                </div>
              )}
              {purchase.items.map((l, i) => (
                <div key={i} style={row}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{l.product_name}{l.is_red_bridge ? ' ✓' : ''}</div>
                    <div style={muted}>{l.quantity} @ {money(l.unit_price)}{l.weight_lbs ? ` · ${Number(l.weight_lbs)} lb` : ''}</div>
                  </div>
                  <div style={{ fontVariantNumeric: 'tabular-nums' }}>{money(l.total_price)}</div>
                </div>
              ))}
              <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 6, paddingTop: 8, fontSize: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><span>{money(purchase.subtotal)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Tax</span><span>{money(purchase.tax)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: 4, fontSize: 16 }}><span>Total</span><span>{money(purchase.total)}</span></div>
              </div>
              {purchase.red_bridge_bags > 0 && <div style={{ ...muted, marginTop: 8 }}>{purchase.red_bridge_bags} Red Bridge bag{purchase.red_bridge_bags === 1 ? '' : 's'} toward your free bag.</div>}
            </>
          )}
        </div>

        <div style={{ padding: '10px 16px 14px', borderTop: '1px solid #eee', flex: '0 0 auto' }}>
          <button type="button" onClick={onClose} style={{ width: '100%', minHeight: 44, borderRadius: 8, border: `1px solid ${RED}`, background: '#fff', color: RED, fontWeight: 700, cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptDialog;
